<template>
  <div class="flex flex-col gap-6 w-full text-slate-700">
    <div class="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5">
      <!-- 1. Total Bets & Total Wins -->
      <div class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 sm:p-5 flex flex-col justify-between gap-4">
        <div class="flex items-center justify-between gap-2 pb-2 border-b border-slate-200/60">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-800 m-0">
            Total Bets & Wins
          </h3>
          <div class="flex items-center gap-3 text-xs">
            <span class="flex items-center gap-1.5 font-semibold text-blue-600">
              <span class="w-2 h-2 rounded-full bg-blue-500"></span> Cashout 1
            </span>
            <span class="flex items-center gap-1.5 font-semibold text-emerald-600">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span> Cashout 2
            </span>
          </div>
        </div>

        <apexchart
          type="area"
          id="apex-bet-win"
          height="240"
          :options="getBetsWinsChartOptions()"
          :series="getBetsWinsSeries()"
        />

        <div class="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200/60">
          <div class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col gap-1">
            <span class="text-[10px] font-bold text-blue-600 uppercase">Cashout 1</span>
            <div class="flex justify-between text-xs font-semibold">
              <span class="text-slate-500">Bets: <strong class="text-slate-800">{{ cashout1Data?.totalBets?.toLocaleString() || 0 }}</strong></span>
              <span class="text-slate-500">Wins: <strong class="text-emerald-600">{{ cashout1Data?.totalWins?.toLocaleString() || 0 }}</strong></span>
            </div>
          </div>
          <div class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col gap-1">
            <span class="text-[10px] font-bold text-emerald-600 uppercase">Cashout 2</span>
            <div class="flex justify-between text-xs font-semibold">
              <span class="text-slate-500">Bets: <strong class="text-slate-800">{{ cashout2Data?.totalBets?.toLocaleString() || 0 }}</strong></span>
              <span class="text-slate-500">Wins: <strong class="text-emerald-600">{{ cashout2Data?.totalWins?.toLocaleString() || 0 }}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <!-- 2. Bet Amount & Win Amount -->
      <div class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 sm:p-5 flex flex-col justify-between gap-4">
        <div class="flex items-center justify-between gap-2 pb-2 border-b border-slate-200/60">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-800 m-0">
            Bet & Win Amount
          </h3>
          <div class="flex items-center gap-3 text-xs">
            <span class="flex items-center gap-1.5 font-semibold text-blue-600">
              <span class="w-2 h-2 rounded-full bg-blue-500"></span> Cashout 1
            </span>
            <span class="flex items-center gap-1.5 font-semibold text-emerald-600">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span> Cashout 2
            </span>
          </div>
        </div>

        <apexchart
          type="bar"
          height="240"
          :options="getAmountChartOptions()"
          :series="getAmountSeries()"
        />

        <div class="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200/60">
          <div class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col gap-1">
            <span class="text-[10px] font-bold text-blue-600 uppercase">Cashout 1</span>
            <div class="flex flex-col text-xs">
              <span class="text-slate-500 truncate">Bet: <strong class="text-slate-800">{{ formatCurrency(cashout1Data?.totalBetAmount) }}</strong></span>
              <span class="text-slate-500 truncate">Win: <strong class="text-emerald-600">{{ formatCurrency(cashout1Data?.totalWinAmount) }}</strong></span>
            </div>
          </div>
          <div class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col gap-1">
            <span class="text-[10px] font-bold text-emerald-600 uppercase">Cashout 2</span>
            <div class="flex flex-col text-xs">
              <span class="text-slate-500 truncate">Bet: <strong class="text-slate-800">{{ formatCurrency(cashout2Data?.totalBetAmount) }}</strong></span>
              <span class="text-slate-500 truncate">Win: <strong class="text-emerald-600">{{ formatCurrency(cashout2Data?.totalWinAmount) }}</strong></span>
            </div>
          </div>
        </div>
      </div>

      <!-- 3. Avg Cashout -->
      <div class="bg-slate-50/70 border border-slate-200/80 rounded-2xl p-4 sm:p-5 flex flex-col justify-between gap-4">
        <div class="flex items-center justify-between gap-2 pb-2 border-b border-slate-200/60">
          <h3 class="text-xs font-bold uppercase tracking-wider text-slate-800 m-0">
            Average Cashout
          </h3>
          <div class="flex items-center gap-3 text-xs">
            <span class="flex items-center gap-1.5 font-semibold text-blue-600">
              <span class="w-2 h-2 rounded-full bg-blue-500"></span> Cashout 1
            </span>
            <span class="flex items-center gap-1.5 font-semibold text-emerald-600">
              <span class="w-2 h-2 rounded-full bg-emerald-500"></span> Cashout 2
            </span>
          </div>
        </div>

        <apexchart
          type="bar"
          height="240"
          :options="getAvgCashoutChartOptions()"
          :series="getAvgCashoutSeries()"
        />

        <div class="grid grid-cols-2 gap-3 pt-2 border-t border-slate-200/60">
          <div class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col items-center justify-center">
            <span class="text-[10px] font-bold text-blue-600 uppercase">Cashout 1 Avg</span>
            <span class="text-sm font-bold text-slate-800">{{ cashout1Data?.avgCashout?.toFixed(2) || '0.00' }}x</span>
          </div>
          <div class="bg-white border border-slate-200/60 rounded-xl p-2.5 flex flex-col items-center justify-center">
            <span class="text-[10px] font-bold text-emerald-600 uppercase">Cashout 2 Avg</span>
            <span class="text-sm font-bold text-slate-800">{{ cashout2Data?.avgCashout?.toFixed(2) || '0.00' }}x</span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import ApexCharts from "vue3-apexcharts";

export default {
  name: "PlayerStatisticsGraph",
  components: {
    apexchart: ApexCharts,
  },
  props: {
    data: {
      type: Object,
      required: true,
    },
    rateCurr: {
      type: Number,
      default: 1,
    },
    currency_code: {
      type: String,
      default: "EUR",
    },
  },
  computed: {
    cashout1Data() {
      return this.data?.cashout?.["1"] || {};
    },
    cashout2Data() {
      return this.data?.cashout?.["2"] || {};
    },
  },
  methods: {
    formatCurrency(amount) {
      if (!amount) return `0.00 ${this.currency_code}`;
      const converted = parseFloat(amount) / parseFloat(this.rateCurr);
      const formatted = converted.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      });
      const currencyLabel = this.rateCurr !== 1 ? "EUR" : this.currency_code;
      return `${formatted} ${currencyLabel}`;
    },
    formatNumber(value) {
      return Number(value || 0).toFixed(2);
    },
    getBetsWinsSeries() {
      return [
        {
          name: "Cashout 1",
          data: [this.cashout1Data.totalBets || 0, this.cashout1Data.totalWins || 0],
        },
        {
          name: "Cashout 2",
          data: [this.cashout2Data.totalBets || 0, this.cashout2Data.totalWins || 0],
        },
      ];
    },
    getBetsWinsChartOptions() {
      return {
        chart: {
          stacked: true,
          background: "transparent",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        colors: ["#3b82f6", "#10b981"],
        stroke: { curve: "smooth", width: 3 },
        markers: { size: 4, strokeWidth: 2, strokeColors: "#fff" },
        dataLabels: { enabled: false },
        xaxis: {
          categories: ["TOTAL BETS", "TOTAL WINS"],
          labels: {
            style: { colors: "#64748b", fontSize: "11px", fontWeight: "700" },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: "#64748b", fontSize: "11px" },
          },
        },
        grid: {
          borderColor: "#e2e8f0",
          strokeDashArray: 3,
        },
        tooltip: {
          theme: "light",
          y: { formatter: (val) => this.formatNumber(val) },
        },
        legend: { show: false },
      };
    },
    getAmountSeries() {
      return [
        {
          name: "Cashout 1",
          data: [
            this.cashout1Data.totalBetAmount || 0,
            this.cashout1Data.totalWinAmount || 0,
          ],
        },
        {
          name: "Cashout 2",
          data: [
            this.cashout2Data.totalBetAmount || 0,
            this.cashout2Data.totalWinAmount || 0,
          ],
        },
      ];
    },
    getAmountChartOptions() {
      return {
        chart: {
          background: "transparent",
          toolbar: { show: false },
        },
        colors: ["#3b82f6", "#10b981"],
        plotOptions: {
          bar: { borderRadius: 6, columnWidth: "50%" },
        },
        dataLabels: { enabled: false },
        stroke: { show: true, width: 4, colors: ["transparent"] },
        xaxis: {
          categories: ["BET AMOUNT", "WIN AMOUNT"],
          labels: {
            style: { colors: "#64748b", fontSize: "11px", fontWeight: "700" },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: "#64748b", fontSize: "11px" },
            formatter: (val) => this.formatCurrency(val),
          },
        },
        grid: {
          borderColor: "#e2e8f0",
          strokeDashArray: 3,
        },
        tooltip: {
          theme: "light",
          y: { formatter: (val) => this.formatCurrency(val) },
        },
        legend: { show: false },
      };
    },
    getAvgCashoutSeries() {
      return [
        {
          name: "Cashout 1",
          data: [this.cashout1Data.avgCashout || 0],
        },
        {
          name: "Cashout 2",
          data: [this.cashout2Data.avgCashout || 0],
        },
      ];
    },
    getAvgCashoutChartOptions() {
      return {
        chart: {
          background: "transparent",
          toolbar: { show: false },
          zoom: { enabled: false },
        },
        plotOptions: {
          bar: { borderRadius: 6, columnWidth: "40%" },
        },
        colors: ["#3b82f6", "#10b981"],
        stroke: { show: true, width: 4, colors: ["transparent"] },
        dataLabels: {
          enabled: true,
          formatter: (val) => `${Number(val || 0).toFixed(2)}x`,
          style: { colors: ["#334155"], fontSize: "11px", fontWeight: "700" },
        },
        xaxis: {
          categories: ["AVG CASHOUT"],
          labels: {
            style: { colors: "#64748b", fontSize: "11px", fontWeight: "700" },
          },
          axisBorder: { show: false },
          axisTicks: { show: false },
        },
        yaxis: {
          labels: {
            style: { colors: "#64748b", fontSize: "11px" },
          },
        },
        grid: {
          borderColor: "#e2e8f0",
          strokeDashArray: 3,
        },
        tooltip: {
          theme: "light",
          y: { formatter: (val) => `${Number(val || 0).toFixed(2)}x` },
        },
        legend: { show: false },
      };
    },
  },
};
</script>
